import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { PublicSharingsModel, UpdateLinksModel } from 'src/app/models/share-links.model';
import { SharesService } from 'src/app/pages/shares/shares.service';
import { CommonService } from 'src/app/services/common.service';
import { ModalService } from 'src/app/services/modal.service';
import { NotificationService } from 'src/app/services/notification.service';
@Component({
  selector: 'app-manage-link-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, ReactiveFormsModule, TranslateModule],

  providers: [ModalService, DatePipe],
  templateUrl: './manage-link-modal.component.html',
  styleUrls: ['./manage-link-modal.component.scss'],
})
export class ManageLinkModalComponent implements OnInit {
  @Input() selectedItem?: any;
  @ViewChild('btnCancelRef') btnCancelRef!: ElementRef;

  //string
  modalTitle = this.commonService.getTranslatedMessage('MANAGE_LINK');
  modalId = 'manageLink';
  ipAddress = '';
  selectedDateTime!: string;
  timezone!: string;

  //boolean
  passwordMismatch = false;
  isEncrypted?: boolean;

  //objects
  manageLinkForm!: FormGroup;
  sendedItem?: PublicSharingsModel;
  selectedFromMainPageItem?: any[];
  selectedItemLinkValues: any[] = [];
  incomingData!: PublicSharingsModel;

  subscription: Subscription = new Subscription();

  constructor(
    public commonService: CommonService,
    private datePipe: DatePipe,
    private formBuilder: FormBuilder,
    private notificationService: NotificationService,
    private sharesService: SharesService,
  ) {
    this.timezone = commonService.getCurrentTimezone();
    this.selectedDateTime = this.getNextMonthDateTime();
    this.createManageLinkForm();
  }

  ngOnInit(): void {
    this.subscription = this.commonService.sendListInfoToManageLink.subscribe((isOpened) => {
      this.sendedItem = isOpened;
      this.sendedItem?.hasPassword ? this.manageLinkForm.get('isPasswordSwitchChecked')?.setValue(true) : null;
      this.sendedItem?.allowIp ? this.manageLinkForm.get('isIpSwitchChecked')?.setValue(true) : null;
      this.sendedItem?.downloadLimit ? this.manageLinkForm.get('isDownloadSwitchChecked')?.setValue(true) : null;
      const transformedDate = this.datePipe.transform(this.sendedItem?.expireDate, 'yyyy-MM-ddTHH:mm:ss');
      if (transformedDate && Number(transformedDate.slice(0, 4)) > 2200) {
        this.manageLinkForm.get('selectedDateTime')?.setValue(this.getNextMonthDateTime());
      } else {
        this.manageLinkForm.get('selectedDateTime')?.setValue(transformedDate);
      }
      this.sendedItem && this.sendedItem.downloadLimit === 0
        ? this.manageLinkForm.get('downloadCount')?.setValue(0)
        : this.manageLinkForm.get('downloadCount')?.setValue(this.sendedItem?.downloadLimit);
      if (this.sendedItem?.downloadLimit === 0) this.manageLinkForm.get('isDownloadSwitchChecked')?.setValue(true);
      this.manageLinkForm.get('ipAddress')?.setValue(this.sendedItem?.allowIp);
      this.isEncrypted = this.sendedItem && this.sendedItem.encrypted;
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }

  onPasswordSwitchChange(): void {
    const isSwitchChecked = this.manageLinkForm.get('isPasswordSwitchChecked')?.value;

    if (!isSwitchChecked) {
      this.manageLinkForm.get('password')?.setValue('');
      this.manageLinkForm.get('passwordAgain')?.setValue('');
    }
  }

  createManageLinkForm(): void {
    this.manageLinkForm = this.formBuilder.group(
      {
        isDatePickerSwitchChecked: [true],
        selectedDateTime: [this.selectedDateTime, [Validators.required]],
        isPasswordSwitchChecked: [this.isEncrypted],
        password: [
          null,
          this.isEncrypted
            ? [
                Validators.required,
                Validators.minLength(8),
                Validators.pattern('(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?!.*[^a-zA-Z0-9.\\-~!@#$%*()+=\\[\\]?])(?=.*[.\\-~!@#$%*()+=\\[\\]?]).*'),
              ]
            : [],
        ],
        passwordAgain: [
          null,
          this.isEncrypted
            ? [
                Validators.required,
                Validators.minLength(8),
                Validators.pattern('(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?!.*[^a-zA-Z0-9.\\-~!@#$%*()+=\\[\\]?])(?=.*[.\\-~!@#$%*()+=\\[\\]?]).*'),
              ]
            : [],
        ],
        isIpSwitchChecked: [false],
        ipAddress: ['', [Validators.maxLength(15), Validators.pattern('^(?!0)(?!.*\\.$)((1?\\d?\\d|25[0-5]|2[0-4]\\d)(\\.|$)){4}$')]],
        ipSelect: ['32'],
        isDownloadSwitchChecked: [false],
        downloadCount: null,
      },
      { validators: this.checkPasswordMatch },
    );
  }

  onSubmit(): void {
    if (this.manageLinkForm.valid) {
      const body: UpdateLinksModel = {
        publicShareId: this.sendedItem!.publicShareId,
        password: this.manageLinkForm.get('isPasswordSwitchChecked')?.value
          ? this.manageLinkForm.get('password')?.value === ''
            ? null
            : this.manageLinkForm.get('password')?.value
          : null,
        expireDate: this.datePipe.transform(this.manageLinkForm.get('selectedDateTime')?.value, 'yyyy-MM-ddTHH:mm:ss') + '.00' + this.timezone,
        allowIp: this.manageLinkForm.get('isIpSwitchChecked')?.value ? this.manageLinkForm.get('ipAddress')?.value : null,
        downloadLimit: this.manageLinkForm.get('isDownloadSwitchChecked')?.value ? this.manageLinkForm.get('downloadCount')?.value : null,
        disablePassword: !this.manageLinkForm.get('isPasswordSwitchChecked')?.value,
      };
      this.sharesService.updatePublicShare(body).subscribe({
        next: () => {
          this.btnCancelRef.nativeElement.click();
          this.commonService.manageLinkSuccessResponse.next(this.sendedItem);
        },
      });
      if (!this.passwordMismatch) {
        this.btnCancelRef.nativeElement.click();
      }
    }
    this.manageLinkForm.reset();
    this.keepFormDefaulValues();
  }

  onCancel(): void {
    this.btnCancelRef.nativeElement.click();
    this.manageLinkForm.reset();
    this.keepFormDefaulValues();
  }

  onSelectDateTime(event: any): void {
    const selectedDate = event.target.value;
    this.selectedDateTime = selectedDate;
  }

  checkDate(selectedDate: string): void {
    const currentDate = new Date();
    const selectedDateTime = new Date(selectedDate);
    if (selectedDateTime <= currentDate || selectedDateTime < new Date()) {
      this.manageLinkForm.get('selectedDateTime')?.setValue(this.getCurrentDateTime());
    }
    if (isNaN(selectedDateTime.getFullYear())) {
      this.manageLinkForm.get('selectedDateTime')?.setValue(this.getCurrentDateTime());
    }
  }

  onInput(event: any): void {
    setTimeout(() => {
      const selectedDate = this.manageLinkForm.get('selectedDateTime')?.value;
      this.checkDate(selectedDate);
    }, 1000);
  }

  getCurrentDateTime(): string {
    const currentDate = new Date();
    const oneMonthLater = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    oneMonthLater.setHours(currentDate.getHours());
    oneMonthLater.setMinutes(currentDate.getMinutes() + 10);
    const formattedDate = this.datePipe.transform(oneMonthLater, "yyyy-MM-dd'T'HH:mm")!.toString();

    return formattedDate;
  }

  getNextMonthDateTime(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = ('0' + (now.getMonth() + 2)).slice(-2); //next month
    const day = ('0' + now.getDate()).slice(-2);
    const hours = ('0' + now.getHours()).slice(-2);
    const minutes = ('0' + now.getMinutes()).slice(-2);

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  checkPasswordMatch(formGroup: FormGroup): void {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('passwordAgain')?.value;

    if (password !== confirmPassword) {
      formGroup.get('passwordAgain')?.setErrors({ mismatch: true });
    } else {
      formGroup.get('passwordAgain')?.setErrors(null);
    }
  }

  onInputValidation(): void {
    this.ipAddress = this.ipAddress.replace(/\D/g, '');

    if (this.ipAddress.length > 15) {
      this.ipAddress = this.ipAddress.substr(0, 15);
    }

    const parts = this.ipAddress.match(/.{1,3}/g) || [];
    parts.length = Math.min(parts.length, 4);
    const adjustedParts = parts.map((part, index) => {
      const digits = part.substr(0, 3);
      if (index === parts.length - 1) {
        const lastPart = digits.substr(0, 3);

        return Math.min(+lastPart, 255).toString();
      }

      if (+digits > 255) {
        return '255';
      }

      return digits;
    });
    this.ipAddress = adjustedParts.join('.');
  }

  closeModalButtonTrigger(): void {
    // Modalın kapatılmasıyla ilgili işlemleri burada gerçekleştirin
    this.btnCancelRef.nativeElement.click();
    this.keepFormDefaulValues();
  }

  keepFormDefaulValues(): void {
    this.manageLinkForm.patchValue({
      isDatePickerSwitchChecked: true,
      selectedDateTime: this.getNextMonthDateTime(),
      isPasswordSwitchChecked: false,
      password: '',
      passwordAgain: '',
      isIpSwitchChecked: false,
      ipAddress: '',
      ipSelect: 32,
      isDownloadSwitchChecked: false,
      downloadCount: 1,
    });
  }
}
